import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import { DashboardLayout } from '../../layouts';
import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  AUTH_KEY,
  Button,
  CircularProgress,
  clearAuthCookie,
  getCookieValue,
  windowExists,
} from '@classity-edtech/ui-base';
import { LanguageCode, Roles } from '@classity-edtech/platform-utils';
import { Stack, Typography } from '@mui/material';
import Helmet from '../../components/Helmet';
import { webPaths } from '../../routes/paths';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import {
  ArrowRightIcon,
  Calendar,
  MoreIcon,
  NoMatchBanner,
  Pagination,
  Tabs,
  UsersIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import VideocamIconOutlined from '@mui/icons-material/VideocamOutlined';
import { useGetMyProfileQuery } from '../../services/users';
import { DataList as DataListType, DataListComp } from '../../components/DataList';
import { useProductsQuery } from '../../services/products';
import { useProductEventsQuery } from '../../services/productEvents';
import PendingState from '../../components/PendingState';
import VideoComponent from '../../components/VideoComponent';
import { useLogoutMutation } from '../../services/authentication';

export interface HomeProps extends MuiBoxProps {}

export const K12 = ({ className }: HomeProps) => {
  const limit = 5;
  const defaultLanguage = LanguageCode.EN;
  const navigate = useNavigate();
  const { data: user, isLoading: loadingUser, isFetching: fetchingUser } = useGetMyProfileQuery();
  const [classPage, setClassPage] = useState(1);
  const [lessonPage, setLessonPage] = useState(1);

  const [logout] = useLogoutMutation();

  const dateRange = useMemo(() => {
    return {
      earliest: dayjs().subtract(1, 'year').toISOString(),
      latest: dayjs().add(1, 'year').toISOString(),
    };
  }, []);

  const { data: products, isLoading: loadingProducts } = useProductsQuery({
    page: classPage,
    limit,
  });
  const { data: productEvents, isLoading: loadingProductEvents } = useProductEventsQuery({
    page: lessonPage,
    limit,
    ...dateRange,
  });

  const classTabs = [
    { label: 'Draft', value: 'Draft' },
    { label: 'Unpublished', value: 'Unpublished' },
    { label: 'Published', value: 'Published' },
  ];
  const lessonTabs = [
    { label: 'Draft', value: 'Draft' },
    { label: 'Upcoming', value: 'Upcoming' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' },
  ];
  const helpTabs = [
    { label: 'All', value: 'All' },
    { label: 'Videos', value: 'Videos' },
    { label: 'Articles', value: 'Articles' },
  ];
  const [classCurrentTab, setClassCurrentTab] = useState(classTabs[0]?.value);
  const [lessonCurrentTab, setLessonCurrentTab] = useState(lessonTabs[0]?.value);
  const [helpCurrentTab, setHelpCurrentTab] = useState(helpTabs[0]?.value);

  const handleClassPageClick = (page: number) => {
    setClassPage(page);
  };

  const handleLessonPageClick = (page: number) => {
    setLessonPage(page);
  };

  const filteredClasses = products?.filter(p => {
    const status = p?.productEvents?.length === 24 ? 'Published' : 'Draft';
    return status === classCurrentTab;
  });

  const classDataList = {
    header: 'Classes',
    type: 'dataList',
    tabs: classTabs?.map(tab => ({
      label: tab.label,
      active: classCurrentTab === tab?.value,
      action: () => setClassCurrentTab(tab?.value),
    })),
    column1Header: 'Subject',
    column2Header: 'Lessons',
    transactionButtons: filteredClasses?.map(p => ({
      column1: {
        description: p?.name?.find(n => n?.language === defaultLanguage)?.displayName,
      },
      column2: { description: p?.classroom?.subjectData?.[0]?.name?.text },
      column3: { description: `${p?.productEvents?.length}/24` },
      isActive: false,
      action: () => {},
      icon: (
        <Box
          sx={{
            width: '40px',
            height: '40px',
            borderRadius: '100%',
            background: globalColours['$global-colour-background-neutral-default'],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <UsersIcon sx={{ width: '16px', height: '16px' }} />
        </Box>
      ),
    })),
    newTransactionButton: {
      label: 'Create New Class',
      disabled: false,
      action: () => navigate(`/${webPaths.classQuestionnaire}`),
    },
    currentPage: classPage,
    numPages: (products?.[0] as any)?.pagination?.pages,
    handlePageClick: handleClassPageClick,
  } as DataListType;

  const filteredLessons = productEvents?.filter(p => {
    const isCompleted = p?.objectives?.length && p?.overview?.length && p?.eventId;
    const status = isCompleted ? 'Upcoming' : 'Draft';
    return status === lessonCurrentTab;
  });

  const lessonsArray = filteredLessons?.map(p => {
    const column2Description =
      products
        ?.find(product => product?.productEvents?.some(e => e === p?.id))
        ?.name?.find(p => p?.language === defaultLanguage)?.displayName || 'N/A';

    const column3Description =
      p?.objectives?.length && p?.overview?.length && p?.eventId
        ? 'Completed'
        : !p?.objectives?.length && !p?.overview?.length && !p?.eventId
          ? 'Created'
          : 'In-progress';

    return {
      column1: {
        description: p?.name?.find(n => n?.language === defaultLanguage)?.displayName,
      },
      column2: {
        description:
          lessonCurrentTab === 'Draft'
            ? column2Description
            : dayjs().diff(dayjs(p?.startTime?.dateTime), 'month') > 1
              ? ` on ${dayjs(p?.startTime?.dateTime).format('MMM D')}`
              : dayjs(p?.startTime?.dateTime).fromNow(),
      },
      column3: {
        description:
          lessonCurrentTab === 'Draft'
            ? column3Description
            : dayjs(p?.startTime?.dateTime).format('HH:mm'),
      },
      isActive: false,
      action:
        lessonCurrentTab === 'Draft'
          ? () => navigate(`/${webPaths.lessonQuestionnaire}/${p?.id}`)
          : () => navigate(`/${webPaths.tutorLesson}/${p?.slug}`),
      icon: (
        <Box
          sx={{
            width: '40px',
            height: '40px',
            borderRadius: '100%',
            border: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Calendar sx={{ width: '16px', height: '16px' }} />
        </Box>
      ),
    };
  });

  const handleLogout = async () => {
    const clientId = getCookieValue(AUTH_KEY);

    logout({
      clientId,
    });

    clearAuthCookie();

    navigate(`${webPaths.root}`);
  };

  const profileDropdown = {
    logoutAction: handleLogout,
    memberId: '',
  };

  const lessonDataList = {
    header: 'Lesson plans',
    type: 'dataList',
    tabs: lessonTabs?.map(tab => ({
      label: tab.label,
      active: lessonCurrentTab === tab?.value,
      action: () => setLessonCurrentTab(tab?.value),
    })),
    column1Header: lessonCurrentTab === 'Draft' ? 'Class' : 'When',
    column2Header: lessonCurrentTab === 'Draft' ? 'Progress' : 'Time',
    transactionButtons: lessonsArray,
    newTransactionButton: {
      label: 'Schedule New Lesson',
      disabled: true,
      action: () => {},
    },
    currentPage: lessonPage,
    numPages: (productEvents?.[0] as any)?.pagination?.pages,
    handlePageClick: handleLessonPageClick,
  } as DataListType;

  useEffect(() => {
    if (user && user?.roleId !== Roles.TUTOR && user?.roleId !== Roles.PROSPECT_TUTOR) {
      navigate(`/404`, { replace: true });
    }
  }, [user]);

  return (
    <>
      <Helmet />
      <DashboardLayout profileDropdown={profileDropdown}>
        {loadingUser || fetchingUser || loadingProductEvents || loadingProducts ? (
          <Box
            sx={{
              width: '100%',
              height: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {user?.roleId === Roles.PROSPECT_TUTOR ? (
              <Stack sx={{ gap: 3 }}>
                <Link
                  to={`/${webPaths.tutorDashboard}`}
                  style={{ textDecoration: 'none', width: 'fit-content', cursor: 'pointer' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      items: 'center',
                      color: globalColours['$global-colour-content-primary'],
                      cursor: 'pointer',
                    }}
                  >
                    <ArrowRightIcon
                      sx={{
                        width: '32px',
                        height: '32px',
                        transform: 'rotate(180deg)',
                        mt: '-4px',
                      }}
                    />
                    <Typography variant='subtitle3'>Group Class</Typography>
                  </Box>
                </Link>

                <PendingState />
              </Stack>
            ) : (
              <>
                {!productEvents?.length ? (
                  <Box>
                    <Stack
                      sx={{
                        alignItems: 'center',
                        textAlign: 'center',
                        maxWidth: '300px',
                        mx: 'auto',
                        mt: 5,
                      }}
                    >
                      <NoMatchBanner sx={{ width: '170px', height: '130px' }} />

                      <Typography variant='subtitle5'>
                        Congratulations, let's get started
                      </Typography>

                      <Typography variant='body3'>
                        Create lessons on your integrated Google Calendar. You can review the short
                        help video below.
                      </Typography>

                      <Button
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
                        onClick={() =>
                          (windowExists as any).open(
                            'https://calendar.google.com/calendar/u/0/r',
                            '_blank',
                          )
                        }
                        preceedingIcon={<Calendar sx={{ width: '20px', height: '20px' }} />}
                        disableBottomFixed
                      >
                        <Typography variant='body2SemiBold' sx={{ textTransform: 'uppercase' }}>
                          CREATE SCHEDULE
                        </Typography>
                      </Button>
                    </Stack>

                    <Box sx={{ maxWidth: '710px', mx: 'auto', my: 4 }}>
                      <VideoComponent
                        title='Scheduling lessons'
                        vidSrc='https://videos.pexels.com/video-files/4121740/4121740-sd_640_360_25fps.mp4'
                      />
                    </Box>
                  </Box>
                ) : (
                  <Stack sx={{ gap: 3 }}>
                    <Link
                      to={`/${webPaths.tutorDashboard}`}
                      style={{ textDecoration: 'none', width: 'fit-content', cursor: 'pointer' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 3,
                          items: 'center',
                          color: globalColours['$global-colour-content-primary'],
                          cursor: 'pointer',
                        }}
                      >
                        <ArrowRightIcon
                          sx={{
                            width: '32px',
                            height: '32px',
                            transform: 'rotate(180deg)',
                            mt: '-4px',
                          }}
                        />
                        <Typography variant='subtitle3'>K-12</Typography>
                      </Box>
                    </Link>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: { xs: 'wrap', xl: 'nowrap' } }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                          gap: 2,
                          width: '100%',
                        }}
                      >
                        <DataListComp dataList={classDataList} />
                        <DataListComp dataList={lessonDataList} />
                      </Box>

                      <Box sx={{ width: { xs: '100%', md: '320px' }, ml: 'auto' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant='subtitle5'>Help tutorials</Typography>

                          <MoreIcon sx={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        </Box>
                        <VideoComponent
                          title='Creating a class'
                          vidSrc='https://videos.pexels.com/video-files/4121740/4121740-sd_640_360_25fps.mp4'
                        />

                        <Box sx={{ mt: 4, width: '100%' }}>
                          <Tabs
                            tabs={helpTabs?.map(tab => ({
                              label: tab.label,
                              active: helpCurrentTab === tab?.value,
                              action: () => setHelpCurrentTab(tab?.value),
                            }))}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              p: 1.5,
                              width: 'fit-content',
                              ml: 'auto',
                            }}
                          >
                            <ArrowRightIcon
                              sx={{ width: '16px', height: '16px', transform: 'rotate(-90deg)' }}
                            />
                            <Typography variant='body3SemiBold'>Length</Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              justifyContent: 'space-between',
                              p: 1.5,
                              borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                              borderRadius: '10px',
                              cursor: 'pointer',
                              '&:hover': {
                                background:
                                  globalColours['$global-colour-background-neutral-default'],
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '100%',
                                  border: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <VideocamIconOutlined sx={{ width: '16px', height: '16px' }} />
                              </Box>

                              <Typography variant='body3'>Scheduling lessons</Typography>
                            </Box>

                            <Typography variant='body3'>10:45</Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              justifyContent: 'space-between',
                              p: 1.5,
                              borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                              borderRadius: '10px',
                              cursor: 'pointer',
                              '&:hover': {
                                background:
                                  globalColours['$global-colour-background-neutral-default'],
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '100%',
                                  border: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <VideocamIconOutlined sx={{ width: '16px', height: '16px' }} />
                              </Box>

                              <Typography variant='body3'>Creating a class</Typography>
                            </Box>

                            <Typography variant='body3'>04:33</Typography>
                          </Box>
                          <Pagination
                            sx={{ px: 3.5, py: { xs: 2, md: 1 } }}
                            currentPage={1}
                            handleClick={() => {}}
                            numPages={5}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </DashboardLayout>
    </>
  );
};

K12.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

K12.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(K12)(
  ({ theme }) => `
`,
);
